import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Link } from 'react-router-dom';
import {
  Users,
  Wrench,
  Shield,
  Megaphone,
  Calendar,
  Building2,
  Newspaper,
  Award,
  MessageSquare,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';

const AdminDashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [token]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin w-8 h-8 border-4 border-plp-blue border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const statCards = [
    { label: 'Total Volunteers', value: stats?.volunteers || 0, icon: Users, color: 'bg-purple-500', link: '/admin/volunteers' },
    { label: 'Safety Reports', value: stats?.safety_reports || 0, icon: Shield, color: 'bg-blue-500', link: '/admin/issues' },
    { label: 'Businesses', value: stats?.businesses || 0, icon: Building2, color: 'bg-emerald-500', link: '/admin/businesses' },
    { label: 'Unread Messages', value: stats?.unread_messages || 0, icon: MessageSquare, color: 'bg-amber-500', link: '/admin/messages' },
  ];

  const issueStats = [
    { label: 'Pending', value: stats?.issues?.pending || 0, icon: Clock, color: 'text-slate-500' },
    { label: 'In Progress', value: stats?.issues?.in_progress || 0, icon: AlertTriangle, color: 'text-amber-500' },
    { label: 'Completed', value: stats?.issues?.done || 0, icon: CheckCircle, color: 'text-emerald-500' },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">Admin Dashboard</h1>
        <p className="text-slate-600 mt-1">Welcome back! Here's what's happening in Killarney Connect.</p>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <Link key={stat.label} to={stat.link}>
            <Card className="bg-white hover:shadow-md transition-shadow cursor-pointer" data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-heading font-bold text-slate-900">{stat.value}</p>
                    <p className="text-sm text-slate-500">{stat.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Issues Overview */}
      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Wrench className="w-5 h-5 text-amber-500" />
            Infrastructure Issues Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            {issueStats.map((stat) => (
              <div key={stat.label} className="text-center p-4 bg-slate-50 rounded-xl">
                <stat.icon className={`w-8 h-8 mx-auto mb-2 ${stat.color}`} />
                <p className="text-2xl font-heading font-bold">{stat.value}</p>
                <p className="text-sm text-slate-500">{stat.label}</p>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden flex">
              <div 
                className="bg-slate-400 h-full" 
                style={{ width: `${(stats?.issues?.pending / (stats?.issues?.total || 1)) * 100}%` }}
              ></div>
              <div 
                className="bg-amber-500 h-full" 
                style={{ width: `${(stats?.issues?.in_progress / (stats?.issues?.total || 1)) * 100}%` }}
              ></div>
              <div 
                className="bg-emerald-500 h-full" 
                style={{ width: `${(stats?.issues?.done / (stats?.issues?.total || 1)) * 100}%` }}
              ></div>
            </div>
            <p className="text-center text-sm text-slate-500 mt-2">
              Total: {stats?.issues?.total || 0} issues
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Announcements', count: stats?.announcements || 0, icon: Megaphone, link: '/admin/announcements' },
          { label: 'Events', count: stats?.events || 0, icon: Calendar, link: '/admin/events' },
          { label: 'Community News', count: stats?.news || 0, icon: Newspaper, link: '/admin/news' },
          { label: 'Spotlights', count: stats?.spotlights || 0, icon: Award, link: '/admin/spotlights' },
        ].map((item) => (
          <Link key={item.label} to={item.link}>
            <Card className="bg-white hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <item.icon className="w-5 h-5 text-plp-blue" />
                  <span className="font-medium text-slate-700">{item.label}</span>
                </div>
                <span className="text-lg font-heading font-bold text-slate-900">{item.count}</span>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Total Users */}
      <Card className="bg-gradient-to-r from-plp-blue to-plp-blue-light text-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm">Total Registered Users</p>
              <p className="text-4xl font-heading font-bold mt-1">{stats?.users || 0}</p>
            </div>
            <TrendingUp className="w-12 h-12 text-white/30" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
